import { CrudFilter } from "@refinedev/core";
import { ChangeEvent, useState } from "react";

/**
 * Custom hook to manage single-field search across multiple inputs. This hook ensures that only one search field is active at a time.
 * When the user types in a specific input field, the other fields are automatically reset.
 *
 * @param initialValues - Object containing the initial search field values
 * @param setFilters - Function to update filters used in the data query 
 * @example
 * ```tsx
 * const initialSearchValues = {
 *   name: "",
 *   email: "",
 *   company: "",
 * };
 * 
 * const { searchValues, handleInputChange } = useSingleFieldSearch(initialSearchValues, setFilters);
 * 
 * return (
 *   <>
 *     <input
 *       placeholder="Search by name"
 *       value={searchValues.name}
 *       onChange={handleInputChange("name")}
 *     />
 *     <input
 *       placeholder="Search by email"
 *       value={searchValues.email}
 *       onChange={handleInputChange("email")}
 *     />
 *     <input
 *       placeholder="Search by company"
 *       value={searchValues.company}
 *       onChange={handleInputChange("company")}
 *     />
 *   </>
 * );
 */
export function useSingleFieldSearch<T extends Record<string, string>>(
  initialValues: T,
  setFilters: (filters: CrudFilter[]) => void
) {
  const [searchValues, setSearchValues] = useState<T>(initialValues);

  const clearSearchValue = (field: keyof T) => () => {
    setSearchValues({
      ...searchValues,
      [field]: ""
    });
    setFilters([])
  }

  const handleInputChange = (field: keyof T) => (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setSearchValues((prev) => {
      const resetValues = Object.keys(prev).reduce((acc, key) => {
        const typedKey = key as keyof T;
        acc[typedKey] = key === field ? (value as T[keyof T]) : ("" as T[keyof T]);
        return acc;
      }, {} as T);

      setFilters([
        {
          field: field as string,
          operator: "contains",
          value,
        },
      ]);

      return resetValues;
    });
  };

  return {
    searchValues,
    handleInputChange,
    clearSearchValue
  };
}
